<template>
  <div class="book-suggest-view">
    <Header />

    <main class="suggest-main py-4">
      <div class="container-fluid px-3 px-md-4 px-lg-5 max-w-1440">

        <!-- HERO -->
        <section class="suggest-hero mb-4 d-flex flex-wrap align-items-center gap-3">
          <div class="flex-grow-1" style="min-width: 200px;">
            <div class="hero-eyebrow mb-2">
              <span class="dot-accent"></span>
              <span>Đóng góp thư viện</span>
            </div>
            <h1 class="hero-title mb-1">Đề Xuất Sách Mới</h1>
            <p class="hero-subtitle mb-0">Bạn không tìm thấy cuốn sách yêu thích? Hãy đề xuất cho ban quản trị - toàn bộ đánh giá, cảm nhận và tiến độ của bạn sẽ tự động được lưu vào tủ sách khi sách được duyệt.</p>
          </div>
          <div class="flex-shrink-0">
            <button type="button" class="btn btn-primary rounded-pill px-4 py-2-5 fw-semibold shadow-sm" @click="toggleForm">
              <icon :icon="showForm ? 'fa-solid fa-minus' : 'fa-solid fa-plus'" class="me-2" />
              {{ showForm ? 'Đóng Form' : 'Tạo Đề Xuất' }}
            </button>
          </div>
        </section>

        <!-- FORM -->
        <transition name="fade-slide">
          <section v-if="showForm" ref="formSection" class="suggest-form-card mb-5">
            <div class="d-flex align-items-start justify-content-between pb-3 mb-4 border-bottom">
              <div>
                <div class="form-header-title">Thông tin sách đề xuất</div>
                <div class="form-header-sub">Vui lòng điền thông tin chi tiết để Admin duyệt nhanh chóng</div>
              </div>
              <button type="button" class="btn-close p-2" @click="showForm = false"></button>
            </div>

            <form @submit.prevent="submitSuggest">
              <!-- Basic info -->
              <div class="row g-3 mb-4">
                <div class="col-md-6">
                  <label class="form-label-custom d-block">Tên sách <span class="text-danger">*</span></label>
                  <input v-model.trim="form.bookName" type="text" class="form-control custom-input" placeholder="Nhập tên chính xác của cuốn sách..." required maxlength="500" />
                </div>
                <div class="col-md-6">
                  <label class="form-label-custom d-block">Tác giả</label>
                  <input v-model.trim="form.authorName" type="text" class="form-control custom-input" placeholder="Tên (các) tác giả..." maxlength="200" />
                </div>
                <div class="col-12">
                  <label class="form-label-custom d-block">Tóm tắt / Giới thiệu sách</label>
                  <textarea v-model.trim="form.bookContent" class="form-control custom-textarea" rows="3" placeholder="Mô tả vắn tắt nội dung cuốn sách, lý do bạn đề xuất..."></textarea>
                </div>
              </div>

              <!-- Personal data -->
              <div class="personal-data-box p-3 p-md-4 mb-4">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <icon icon="fa-solid fa-bookmark" class="text-primary" style="font-size: 1rem;" />
                  <span class="fw-bold fs-7" style="color:#1a1a2e;">Đính kèm trải nghiệm đọc & ghi chú</span>
                  <span class="badge-optional">Tùy chọn</span>
                </div>
                <p class="fs-8 text-muted mb-3">Khi sách được duyệt, toàn bộ dữ liệu bên dưới sẽ tự động lưu vào tủ sách của bạn.</p>

                <div class="row g-3 mb-4">
                  <div class="col-md-5">
                    <label class="form-label-custom d-block">Đánh giá</label>
                    <div class="star-rating-box">
                      <button v-for="star in 5" :key="star" type="button" class="btn-star" @mouseenter="hoverRating = star" @mouseleave="hoverRating = 0" @click="setRating(star)">
                        <icon icon="fa-solid fa-star" :class="star <= (hoverRating || form.evaluateNumber) ? 'text-warning' : 'text-star-off'" style="font-size:1.2rem;" />
                      </button>
                      <span v-if="form.evaluateNumber" class="ms-1 fs-7 fw-semibold" style="color:#1a1a2e;">{{ form.evaluateNumber }}/5</span>
                    </div>
                  </div>
                  <div class="col-md-7">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <label class="form-label-custom d-block m-0">Nhận xét về cuốn sách này</label>
                      <span class="char-count ms-auto">{{ form.reviewContent ? form.reviewContent.length : 0 }} / 2000</span>
                    </div>
                    <textarea
                      v-model="form.reviewContent"
                      class="review-textarea"
                      rows="3"
                      placeholder="Cảm nhận của bạn về cuốn sách..."
                      maxlength="2000"
                    ></textarea>
                    <div v-if="selectedStickersMap['suggestReview'] && selectedStickersMap['suggestReview'].length > 0" class="selected-stickers-row mt-2">
                      <div v-for="(sticker, sIndex) in selectedStickersMap['suggestReview']" :key="sIndex" class="selected-sticker-preview position-relative d-inline-block me-2 mb-2">
                        <img v-if="sticker.url" :src="sticker.url" class="sticker-preview-img" style="height: 60px; object-fit: contain;" />
                        <span v-else class="inline-sticker-badge" :class="getStickerThemeClass(sticker)">
                          <span class="sticker-shine"></span>{{ sticker.name || sticker.code }}
                        </span>
                        <button type="button" @click="removeSelectedSticker('suggestReview', sIndex)" class="btn-remove-sticker position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-white" style="z-index: 10;">
                          <icon icon="fa-solid fa-xmark" style="font-size: 0.6rem;" />
                        </button>
                      </div>
                    </div>
                    <div class="form-options-row mt-2">
                      <button type="button" class="btn-emoji-trigger" title="Thêm biểu cảm & nhãn dán" @click.stop="toggleEmojiPicker('suggestReview', $event)">
                        <icon icon="fa-solid fa-face-smile" class="text-warning me-1.5" />
                        <span>Biểu cảm</span>
                      </button>
                      <div class="d-flex align-items-center gap-3 ms-auto">
                        <div class="switch-option">
                          <label class="switch-container">
                            <input type="checkbox" v-model="form.isSpoil" :disabled="!form.reviewContent?.trim()" />
                            <span class="switch-slider" :class="{ 'switch-disabled': !form.reviewContent?.trim() }"></span>
                          </label>
                          <span class="switch-label spoiler-label">Nhận xét có Spoiler</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row g-3 border-top pt-3">
                  <div class="col-md-6">
                    <label class="form-label-custom d-block">Trạng thái đọc</label>
                    <div class="d-flex flex-wrap gap-1-5">
                      <button
                        v-for="st in progressStatuses"
                        :key="st.value"
                        type="button"
                        :class="[
                          'btn-status-pill',
                          {
                            'active': form.progressStatus === st.value,
                            'disabled': hasReviewedOrRated && (st.value === 0 || st.value === 1)
                          }
                        ]"
                        :disabled="hasReviewedOrRated && (st.value === 0 || st.value === 1)"
                        :title="hasReviewedOrRated && (st.value === 0 || st.value === 1) ? 'Không thể chọn trạng thái này khi đã Đánh giá/Nhận xét' : ''"
                        @click="selectProgressStatus(st.value)"
                      >
                        <icon :icon="st.icon" />{{ st.label }}
                      </button>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label-custom d-block">Sở hữu sách</label>
                    <div class="d-flex flex-wrap gap-1-5">
                      <button v-for="own in ownStatuses" :key="own.value" type="button" :class="['btn-own-pill', { 'active': form.ownStatus === own.value }]" @click="form.ownStatus = form.ownStatus === own.value ? null : own.value">
                        <icon :icon="own.icon" />{{ own.label }}
                      </button>
                    </div>
                  </div>

                  <div v-if="form.progressStatus === 2 || form.progressStatus === 4" class="col-12 mt-3 progress-detail-box p-3">
                    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                      <span class="fw-semibold fs-8" style="color:#1a1a2e;">Tiến độ đọc</span>
                      <div class="progress-type-toggle">
                        <button type="button" :class="['btn-toggle-type', { 'active': !form.noteType }]" @click="setNoteType(false)">Theo số trang</button>
                        <button type="button" :class="['btn-toggle-type', { 'active': form.noteType }]" @click="setNoteType(true)">Theo phần trăm (%)</button>
                      </div>
                    </div>
                    <div class="row g-3 align-items-end">
                      <template v-if="!form.noteType">
                        <div class="col-6 col-sm-4">
                          <label class="fs-8 text-muted mb-1 d-block">Trang hiện tại</label>
                          <input type="number" class="form-control progress-input-number" v-model.number="form.currentPage" min="0" :max="form.totalPage" @input="calculatePercent" />
                        </div>
                        <div class="col-6 col-sm-4">
                          <label class="fs-8 text-muted mb-1 d-block">Tổng số trang</label>
                          <input type="number" class="form-control progress-input-number" v-model.number="form.totalPage" min="0" @input="calculatePercent" />
                        </div>
                      </template>
                      <template v-else>
                        <div class="col-12 col-sm-8">
                          <div class="d-flex justify-content-between align-items-center mb-1">
                            <label class="fs-8 text-muted">Phần trăm hoàn thành</label>
                          </div>
                          <div class="d-flex align-items-center gap-3">
                            <input type="range" class="form-range progress-slider flex-grow-1" v-model.number="form.percent" @input="onPercentInput" min="0" max="100" step="0.1" />
                            <div class="percent-input-box">
                              <input type="number" class="percent-input-field" v-model.number="form.percent" @input="onPercentInput" min="0" max="100" step="0.1" placeholder="0" />
                              <span class="percent-badge-unit">%</span>
                            </div>
                          </div>
                        </div>
                      </template>
                      <div class="col-12 mt-3">
                        <div class="custom-progress-track">
                          <div class="custom-progress-bar" :style="{ width: progressPercent + '%' }"></div>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mt-1 fs-8 text-muted">
                          <span>Tiến độ: <strong style="color:#1e3e3f;">{{ progressPercent }}%</strong></span>
                          <span v-if="form.totalPage > 0">{{ form.currentPage }} / {{ form.totalPage }} trang</span>
                        </div>
                      </div>
                      <div v-if="form.progressStatus === 2 && !form.noteType && form.totalPage > 0 && form.currentPage >= form.totalPage" class="suggest-read-badge d-flex align-items-center gap-2 mt-2 mb-0">
                        <icon icon="fa-solid fa-circle-check" />
                        <span>Bạn đã đọc hết sách! Nhấn <strong @click="form.progressStatus = 3">Đã đọc xong</strong> để cập nhật trạng thái.</span>
                      </div>
                      <div v-if="form.progressStatus === 2 && form.noteType && form.percent >= 100" class="suggest-read-badge d-flex align-items-center gap-2 mt-2 mb-0">
                        <icon icon="fa-solid fa-circle-check" />
                        <span>Bạn đã đọc hết sách! Nhấn <strong @click="form.progressStatus = 3">Đã đọc xong</strong> để cập nhật trạng thái.</span>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 mt-3">
                    <label class="form-label-custom d-block">Ghi chú cá nhân</label>
                    <input v-model="form.note" type="text" class="form-control custom-input" placeholder="Trích dẫn tâm đắc, vị trí đặt mua, giá bìa..." maxlength="500" />
                  </div>
                </div>
              </div>

              <!-- EMOJI & STICKER PICKER POPOVER -->
              <transition name="fade-slide">
                <div v-if="showEmojiPicker" class="emoji-sticker-picker-popover" :style="emojiPickerPos" @click.stop>
                  <div class="picker-header">
                    <div class="picker-tabs">
                      <button type="button" :class="['btn-picker-tab', { active: emojiPickerTab === 'emojis' }]" @click="emojiPickerTab = 'emojis'">
                        <span>😃 Biểu cảm</span>
                      </button>
                      <button type="button" :class="['btn-picker-tab', { active: emojiPickerTab === 'stickers' }]" @click="emojiPickerTab = 'stickers'">
                        <span>🐸 Nhãn dán hài hước</span>
                      </button>
                    </div>
                    <button type="button" class="btn-close-picker" title="Đóng" @click="closeEmojiPicker">
                      <icon icon="fa-solid fa-xmark" />
                    </button>
                  </div>
                  <div class="picker-search-wrapper">
                    <icon icon="fa-solid fa-magnifying-glass" class="picker-search-icon" />
                    <input v-model="emojiPickerSearch" type="text" class="picker-search-input" :placeholder="emojiPickerTab === 'stickers' ? 'Tìm nhãn dán GIF (VD: Mèo, Pepe, Khóc)...' : 'Tìm biểu cảm emoji...'" />
                    <button v-if="emojiPickerSearch" type="button" class="btn-clear-search" @click="emojiPickerSearch = ''">
                      <icon icon="fa-solid fa-circle-xmark" />
                    </button>
                  </div>
                  <div v-if="emojiPickerTab === 'emojis'" class="picker-body">
                    <div v-for="(cat, idx) in emojiCategories" :key="'cat-' + idx" class="emoji-cat-section">
                      <div class="cat-title"><span>{{ cat.icon }} {{ cat.name }}</span></div>
                      <div class="emoji-grid">
                        <button v-for="(emoji, eIdx) in cat.list" :key="'e-' + eIdx" type="button" class="btn-emoji-item" @click="insertEmoji(emoji)">{{ emoji }}</button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="picker-body stickers-body">
                    <div class="animated-stickers-grid">
                      <button v-for="st in filteredStickers" :key="st.id" type="button" class="btn-animated-sticker-card" @click="insertSticker(st)">
                        <div class="sticker-gif-thumb"><img :src="st.url" :alt="st.name" /></div>
                        <span class="sticker-gif-name">{{ st.name }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </transition>

              <!-- Actions -->
              <div class="d-flex justify-content-end gap-2">
                <button type="button" class="btn btn-outline-secondary rounded-pill px-4 btn-cancel" @click="showForm = false">Hủy</button>
                <button type="submit" class="btn btn-primary rounded-pill px-5 fw-semibold" :disabled="!formValid || isSubmitting">
                  <icon v-if="isSubmitting" icon="fa-solid fa-spinner" class="fa-spin me-1" />
                  Gửi Đề Xuất
                </button>
              </div>
            </form>
          </section>
        </transition>

        <!-- SUGGESTIONS LIST -->
        <section>
          <div class="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
            <h2 class="fs-6 fw-bold m-0" style="color:#1a1a2e;">Lịch Sử Đề Xuất</h2>
            <span class="fs-8 text-muted">{{ totalElements }} đề xuất</span>
          </div>

          <!-- Loading -->
          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary spinner-border-sm"></div>
            <p class="text-muted fs-8 mt-2">Đang tải...</p>
          </div>

          <!-- Empty -->
          <div v-else-if="mySuggests.length === 0" class="empty-state-box text-center py-5 my-3 rounded-3">
            <div class="empty-state-icon mb-3"><icon icon="fa-solid fa-book-medical" /></div>
            <h3 class="fs-6 fw-bold" style="color:#1a1a2e;">Bạn chưa gửi đề xuất nào</h3>
            <p class="fs-8 text-muted mb-3 px-3" style="max-width: 380px; margin:0 auto;">Hãy giúp phong phú thêm thư viện bằng cách đề xuất những cuốn sách hay mà bạn đang tìm kiếm!</p>
            <button type="button" class="btn btn-primary btn-sm rounded-pill px-4" @click="toggleForm">Tạo đề xuất ngay</button>
          </div>

          <!-- Table -->
          <div v-else class="suggest-table-wrap">
            <table class="suggest-table">
              <thead>
                <tr>
                  <th class="th-status">Trạng thái</th>
                  <th class="th-book">Sách</th>
                  <th class="th-author">Tác giả</th>
                  <th class="th-attach">Đính kèm</th>
                  <th class="th-date">Ngày tạo</th>
                  <th class="th-actions">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in mySuggests" :key="item.id" class="suggest-table-row">
                  <td class="td-status" data-label="Trạng thái">
                    <span :class="['badge-status', getStatusClass(item.status)]">{{ getStatusLabel(item.status) }}</span>
                  </td>
                  <td class="td-book" data-label="Sách">
                    <div class="td-book-name truncate-2">{{ item.bookName }}</div>
                  </td>
                  <td class="td-author" data-label="Tác giả">
                    <span class="td-author-text">{{ item.authorName || 'Chưa cập nhật' }}</span>
                  </td>
                  <td class="td-attach" data-label="Đính kèm">
                    <span v-if="hasAttachments(item)" class="attach-yes">
                      <icon icon="fa-solid fa-paperclip" class="me-1" />Có
                    </span>
                    <span v-else class="attach-no">—</span>
                  </td>
                  <td class="td-date" data-label="Ngày tạo">{{ formatDateTime(item.createTime) }}</td>
                  <td class="td-actions" data-label="Thao tác">
                    <div class="d-inline-flex align-items-center gap-2 flex-wrap">
                      <router-link v-if="item.status === 1 && item.approvedBookId" :to="'/book/' + item.approvedBookId" class="btn-action-outline">
                        Xem sách <icon icon="fa-solid fa-arrow-right" class="ms-1" />
                      </router-link>
                      <button type="button" class="btn-action-detail" @click="openDetail(item)">
                        <icon icon="fa-solid fa-eye" class="me-1" />Xem chi tiết
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <nav v-if="totalPages > 1" class="d-flex justify-content-center mt-4 mb-3">
            <ul class="pagination pagination-sm">
              <li class="page-item" :class="{ disabled: currentPage === 0 }">
                <button class="page-link" @click="goToPage(currentPage - 1)">Trang trước</button>
              </li>
              <li v-for="p in visiblePages()" :key="p" class="page-item" :class="{ active: p === currentPage }">
                <button class="page-link" @click="goToPage(p)">{{ p + 1 }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages - 1 }">
                <button class="page-link" @click="goToPage(currentPage + 1)">Trang sau</button>
              </li>
            </ul>
          </nav>
        </section>

      </div>
    </main>

    <!-- DETAIL MODAL -->
    <transition name="fade">
      <div v-if="showDetailModal && detailItem" class="custom-modal-backdrop">
        <div class="modal-double-bezel animate-slide-up">
          <div class="custom-modal-card p-0">
            <!-- Modal Header -->
            <div class="modal-header-hero p-4 pb-3 d-flex align-items-start justify-content-between border-bottom">
              <div class="pe-3">
                <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <span :class="['badge-status', getStatusClass(detailItem.status)]">
                    <icon :icon="detailItem.status === 1 ? 'fa-solid fa-circle-check' : detailItem.status === 2 ? 'fa-solid fa-circle-xmark' : 'fa-solid fa-clock'" class="me-1.5" />
                    {{ getStatusLabel(detailItem.status) }}
                  </span>
                  <span class="modal-date-chip">
                    <icon icon="fa-solid fa-clock" class="me-1.5" />{{ formatDateTime(detailItem.createTime) }}
                  </span>
                </div>
                <h3 class="modal-book-title m-0">{{ detailItem.bookName }}</h3>
                <div class="modal-author-tag mt-2">
                  <icon icon="fa-solid fa-user-pen" class="me-2 text-teal" />
                  <span>Tác giả: <strong>{{ detailItem.authorName || 'Chưa cập nhật' }}</strong></span>
                </div>
              </div>
              <button type="button" class="modal-btn-close" title="Đóng" @click="closeDetailModal">
                <icon icon="fa-solid fa-xmark" />
              </button>
            </div>

            <!-- Modal Content Body -->
            <div class="modal-body-content p-4">
              <!-- Admin Rejection Note if rejected -->
              <div v-if="detailItem.status === 2 && detailItem.adminNote" class="modal-admin-rejection-card mb-4 p-3 rounded-3">
                <div class="d-flex align-items-start gap-2">
                  <icon icon="fa-solid fa-triangle-exclamation" class="text-danger fs-6 mt-1 me-2 flex-shrink-0" />
                  <div>
                    <div class="fw-bold text-danger fs-8 text-uppercase tracking-wide mb-1">Lý do từ chối đề xuất</div>
                    <div class="fs-8 text-dark">{{ detailItem.adminNote }}</div>
                  </div>
                </div>
              </div>

              <!-- Book Summary / Description -->
              <div v-if="detailItem.bookContent" class="modal-section-card mb-4 p-3 rounded-3">
                <div class="modal-section-title mb-2">
                  <icon icon="fa-solid fa-align-left" class="me-2 text-teal" />Mô tả sách đề xuất
                </div>
                <div class="modal-book-desc m-0 fs-8" v-html="formatContentHtml(detailItem.bookContent)"></div>
              </div>

              <!-- Personal Data Attachments -->
              <div class="modal-section-card p-3 rounded-3">
                <div class="modal-section-title mb-3 d-flex align-items-center justify-content-between">
                  <span><icon icon="fa-solid fa-paperclip" class="me-2 text-teal" />Dữ liệu cá nhân đính kèm</span>
                  <span v-if="hasAttachments(detailItem)" class="badge bg-teal-subtle text-teal border border-teal-subtle rounded-pill fs-9 px-2 py-1">Có dữ liệu</span>
                </div>

                <div class="row g-3">
                  <!-- Rating Tile -->
                  <div class="col-sm-6 col-md-4">
                    <div class="modal-metric-tile">
                      <span class="metric-label">Đánh giá</span>
                      <div v-if="detailItem.evaluateNumber" class="d-flex align-items-center gap-1 mt-1">
                        <div class="stars-display me-1">
                          <icon v-for="s in 5" :key="s" icon="fa-solid fa-star" :class="s <= detailItem.evaluateNumber ? 'text-warning' : 'text-muted-light'" />
                        </div>
                        <span class="fw-bold fs-8 text-dark">{{ detailItem.evaluateNumber }}/5</span>
                      </div>
                      <div v-else class="text-muted fs-8 mt-1">Chưa đánh giá</div>
                    </div>
                  </div>

                  <!-- Reading Progress Tile -->
                  <div class="col-sm-6 col-md-4">
                    <div class="modal-metric-tile">
                      <span class="metric-label">Trạng thái đọc</span>
                      <div class="fw-semibold fs-8 text-dark mt-1 d-flex align-items-center">
                        <icon icon="fa-solid fa-book-open-reader" class="text-teal fs-8 me-2" />
                        <span>{{ getProgressLabel(detailItem.progressStatus) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Ownership Tile -->
                  <div class="col-sm-6 col-md-4">
                    <div class="modal-metric-tile">
                      <span class="metric-label">Sở hữu sách</span>
                      <div class="fw-semibold fs-8 text-dark mt-1 d-flex align-items-center">
                        <icon icon="fa-solid fa-boxes-stacked" class="text-teal fs-8 me-2" />
                        <span>{{ getOwnLabel(detailItem.ownStatus) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Review Content -->
                <div v-if="detailItem.reviewContent" class="mt-3 pt-3 border-top">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <span class="metric-label d-flex align-items-center">
                      <icon icon="fa-solid fa-quote-left" class="text-teal me-2" />Nhận xét cá nhân
                    </span>
                    <span v-if="detailItem.isSpoil" class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle rounded-pill fs-9">Có Spoil</span>
                  </div>
                  <div class="modal-review-quote p-3 rounded-3 fs-8" v-html="formatContentHtml(detailItem.reviewContent)"></div>
                </div>

                <!-- Personal Note -->
                <div v-if="detailItem.note" class="mt-3 pt-3 border-top">
                  <span class="metric-label d-flex align-items-center mb-2">
                    <icon icon="fa-solid fa-note-sticky" class="text-teal me-2" />Ghi chú cá nhân
                  </span>
                  <div class="modal-note-box p-3 rounded-3 fs-8 text-dark" v-html="formatContentHtml(detailItem.note)"></div>
                </div>
              </div>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer-action p-3 px-4 bg-light-subtle rounded-bottom-4 border-top d-flex align-items-center justify-content-between">
              <div v-if="detailItem.status === 1 && detailItem.approvedBookId">
                <router-link :to="'/book/' + detailItem.approvedBookId" class="btn-action-outline-modal text-decoration-none">
                  <span>Xem sách</span>
                  <div class="icon-circle ms-2">
                    <icon icon="fa-solid fa-arrow-right" />
                  </div>
                </router-link>
              </div>
              <div v-else class="text-muted fs-8"></div>
              <button type="button" class="modal-btn-dismiss" @click="closeDetailModal">Đóng</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <Footer />
  </div>
</template>

<script src="./book-suggest.js"></script>
<style src="./book-suggest.css" scoped></style>
